import { getAccessTokenSilentlyWithProviderDefaults } from '../utils/auth0SilentToken';

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export const resolveTenantAuthApiBaseUrl = () => {
  const configured = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL_RAILWAY;
  if (configured) return configured.replace(/\/$/, '');
  if (typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    return `http://${window.location.hostname}:5050`;
  }
  return typeof window !== 'undefined' ? window.location.origin.replace(/\/$/, '') : '';
};

const tenantAuthError = (payload, fallback, status) => {
  const error = new Error(payload?.error || fallback);
  error.code = payload?.code || 'TENANT_REQUEST_FAILED';
  error.details = payload?.details;
  error.status = status;
  return error;
};

const legacyAuthorization = async (getAccessTokenSilently, enabled) => {
  if (!enabled || typeof getAccessTokenSilently !== 'function') return {};
  try {
    const token = await getAccessTokenSilentlyWithProviderDefaults(getAccessTokenSilently, {
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE },
    });
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_error) {
    return {};
  }
};

export async function loadTenantIdentitySession({ getAccessTokenSilently, hasAuth0Session = false } = {}) {
  const response = await fetch(`${resolveTenantAuthApiBaseUrl()}/api/auth/tenant/session`, {
    credentials: 'include',
    headers: await legacyAuthorization(getAccessTokenSilently, hasAuth0Session),
  });
  const payload = await response.json().catch(() => ({ success: false, error: 'Unable to verify owner access.' }));
  if (!response.ok) throw tenantAuthError(payload, 'Unable to verify owner access.', response.status);
  return payload.data;
}

export async function tenantIdentityRequest(
  session,
  getAccessTokenSilently,
  endpoint,
  options = {},
) {
  const method = String(options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers || {});
  const legacyHeaders = await legacyAuthorization(
    getAccessTokenSilently,
    session?.provider === 'auth0',
  );
  Object.entries(legacyHeaders).forEach(([name, value]) => headers.set(name, value));
  if (MUTATION_METHODS.has(method) && session?.csrfToken) {
    headers.set('X-TPH-CSRF', session.csrfToken);
  }
  const response = await fetch(`${resolveTenantAuthApiBaseUrl()}${endpoint}`, {
    ...options,
    method,
    credentials: 'include',
    headers,
  });
  const payload = await response.json().catch(() => ({ success: false, error: 'Tenant request failed.' }));
  if (!response.ok) throw tenantAuthError(payload, 'Tenant request failed.', response.status);
  return payload;
}

export function buildTenantWorkOSLoginUrl(returnTo = '/tenant') {
  const safeReturnTo = typeof returnTo === 'string' && returnTo.startsWith('/') && !returnTo.startsWith('//')
    ? returnTo
    : '/tenant';
  return `${resolveTenantAuthApiBaseUrl()}/api/auth/tenant/workos/login?returnTo=${encodeURIComponent(safeReturnTo)}`;
}

export function beginTenantWorkOSLogin(returnTo = '/tenant') {
  window.location.assign(buildTenantWorkOSLoginUrl(returnTo));
}

export async function requestTenantInvitationRecovery(email = '', challengeToken = '') {
  const normalizedEmail = String(email || '').trim();
  const normalizedChallengeToken = String(challengeToken || '').trim();
  const hasEmail = normalizedEmail.length > 0;
  const requestBody = hasEmail
    ? {
      email: normalizedEmail,
      ...(normalizedChallengeToken ? { challengeToken: normalizedChallengeToken } : {}),
    }
    : null;
  const response = await fetch(
    `${resolveTenantAuthApiBaseUrl()}/api/auth/tenant/workos/invitations/request-new`,
    {
      method: 'POST',
      credentials: 'include',
      headers: hasEmail ? { 'Content-Type': 'application/json' } : undefined,
      body: requestBody ? JSON.stringify(requestBody) : undefined,
    },
  );
  const payload = await response.json().catch(() => ({
    success: false,
    error: 'Unable to request a new owner invitation.',
  }));
  if (!response.ok) {
    throw tenantAuthError(payload, 'Unable to request a new owner invitation.', response.status);
  }
  return payload.data;
}

export async function refreshTenantIdentitySession(session, getAccessTokenSilently) {
  const payload = await tenantIdentityRequest(
    session,
    getAccessTokenSilently,
    '/api/auth/tenant/session/refresh',
    { method: 'POST' },
  );
  return payload.data;
}

export async function logoutTenantIdentitySession(session, getAccessTokenSilently) {
  const payload = await tenantIdentityRequest(
    session,
    getAccessTokenSilently,
    '/api/auth/tenant/logout',
    { method: 'POST' },
  );
  return payload.data;
}
